import React from 'react';

import HubspotForm from '@videoblocks/shared-components/dist/mjs/components/Shared/HubspotForm';
import { ButtonColor, ButtonVariant } from '@videoblocks/storywind';

import SiteConstants, {
  BlogResourceTypes,
} from '../../common/SiteConstants/SiteConstants';
import ChiliPiperLoader from '../../common/components/ChiliPiperLoader';
import FeatureList from '../components/FeatureList';
import ImageAndText from '../components/ImageAndText';

const MakerForTeamsLandingPageContainer = () => {
  const featureList = [
    {
      icon: '/assets/common/images/MakerForTeams/icons/design-tool-pencil-ruler.SVG',
      iconalt: 'design-tool-pencil-ruler',
      heading: 'Collaboration tools',
      text: 'Share content for easy collaboration',
    },

    {
      icon: '/assets/common/images/MakerForTeams/icons/library-school-learning-contrast.SVG',
      iconalt: 'library-school-learning',
      heading: 'Unlimited content library',
      text: 'Access millions of video and audio clips',
    },

    {
      icon: '/assets/common/images/MakerForTeams/icons/color-painting-palette.SVG',
      iconalt: 'color-painting-palette',
      heading: 'One-Click branding',
      text: 'Apply branding to videos with one click',
    },

    {
      icon: '/assets/common/images/MakerForTeams/icons/video-interface-essential-contrast.SVG',
      iconalt: 'video-interface-essential',
      heading: 'Custom templates',
      text: 'Save time with custom templates for infinite re-use',
    },
  ];

  interface TipsAndTutorialsCardProps {
    href: string;
    src: string;
    thumbnailAlt: string;
    text: string;
  }

  const TipsAndTutorialsCard = (props: TipsAndTutorialsCardProps) => {
    return (
      <>
        <a href={props.href} className={'w-auto md:w-1/3 p-3 text-black'}>
          <img
            className="h-auto max-w-lg w-full"
            src={props.src}
            alt={props.thumbnailAlt}
            width={288}
            height={134}
          />
          <p className="text-lg">{props.text}</p>
        </a>
      </>
    );
  };
  return (
    <>
      <ImageAndText
        darkMode={true}
        h1Header={true}
        heading="Simplify and scale video creation in minutes"
        content="Maker for Teams makes video creation accessible with a simple editor and business-exclusive features to streamline the creation process. "
        showCallToAction={true}
        callToActionColor={ButtonColor.Primary}
        callToActionText="Request a Demo"
        callToActionHref="#leadForm"
        wistiaSrc="fd647g2kmp"
      />
      <FeatureList
        heading="Maker for Teams"
        showCallToAction={true}
        callToActionColor={ButtonColor.Secondary}
        callToActionText="Request a Demo"
        callToActionHref="#leadForm"
        callToActionType={ButtonVariant.Outlined}
        featureBullets={featureList}
      />
      <ImageAndText
        heading="One-click branding"
        content="Use the Brands feature to set custom colors, fonts, and logos for teams to use, ensuring consistency in all your videos and speeding up the creation process."
        imagesFirst={true}
        imageSrc="/assets/common/images/MakerForTeams/One-click branding.gif"
        imageSourceAlt="View of three brand style options next to the Maker editor showing a video preview with a timeline editor underneath."
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/MakerForTeams/icons/color-painting-palette.SVG',
        ]}
        sectionIconAlt={['']}
      />
      <ImageAndText
        darkMode={true}
        heading="Custom templates"
        content="Create custom video templates with pre-set fonts, colors, timing, and logos to create faster than ever. Templates allow even non-creatives to make videos easily and at scale."
        imageSrc="/assets/common/images/MakerForTeams/Custom templates.png"
        imageSourceAlt="View of a selected Maker Template and it’s available actions next to the Maker editor showing a video preview of the selected Template.Y"
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/MakerForTeams/icons/video-interface-essential.SVG',
        ]}
        sectionIconAlt={['']}
      />
      <ImageAndText
        heading="Collaboration tools"
        content="Built for effortless collaboration across organizations of any size, any team member can share projects, videos, templates, and folders with colleagues to ensure consistency across projects."
        imagesFirst={true}
        imageSrc="/assets/common/images/MakerForTeams/Collaborative Tools.gif"
        imageSourceAlt="A view of a overlapping cards containing previews of features available to ‘Maker for Teams’ users next to a card containing a ‘Team Access’ toggle."
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/MakerForTeams/icons/design-tool-pencil-ruler.SVG',
        ]}
        sectionIconAlt={['']}
      />
      <ImageAndText
        darkMode={true}
        heading="Unlimited content library"
        content="Maker for Teams is directly connected to Storyblocks’ unlimited content library, giving you access to millions of footage, music, images, sound effects, and more to use and test in your videos."
        showCallToAction={true}
        callToActionColor={ButtonColor.Primary}
        callToActionText="Request a Demo"
        callToActionHref="#leadForm"
        imageSrc="/assets/common/images/MakerForTeams/Unlimited content library.png"
        imageSourceAlt="A masonry grid of cards that contain a collection of stock images and previews of an audio player."
        showSectionIcon={true}
        sectionIconSrc={[
          '/assets/common/images/MakerForTeams/icons/library-school-learning.SVG',
        ]}
        sectionIconAlt={['']}
      />
      <div
        id="leadForm"
        style={{ scrollMarginTop: 100 }}
        className="mb-10 md:mb-20"
      >
        <h1 className="text-center container mx-auto">
          Imagine your company with Maker for Teams
        </h1>
        <HubspotForm formId="211ff9a3-5321-4672-a8a0-5d16378c9f05" />
        <ChiliPiperLoader />
      </div>
      <div className="bg-blue-100 p-8 md:p-16 lg:p-24">
        <h2
          className="
                text-3xl lg:text-5xl
                text-center
                container
                mx-auto
                my-auto
                font-bold
                mb-6
                font-serif
                bg-transparent"
        >
          Tips & tutorials
        </h2>
        <div className="flex justify-center md:justify-between flex-col md:flex-row">
          <TipsAndTutorialsCard
            href={`${SiteConstants.getBlogUrl(
              BlogResourceTypes.News
            )}/introducing-maker-brands`}
            src={'/assets/common/images/maker-brands.png'}
            thumbnailAlt={'Maker Brands'}
            text={'Introducing: Maker Brands!'}
          />
          <TipsAndTutorialsCard
            href={`${SiteConstants.getBlogUrl(
              BlogResourceTypes.News
            )}/intro-to-video-editor-maker`}
            src={'/assets/common/images/maker-tutorial.png'}
            thumbnailAlt={'Maker Tutorial'}
            text={
              'Start making videos easily and quickly with Maker: Storyblocks’ online video editor'
            }
          />
          <TipsAndTutorialsCard
            href={'https://youtu.be/_QyNcuBJAvo'}
            src={'/assets/common/images/introducing-maker.png'}
            thumbnailAlt={'Introducing Maker'}
            text={'Introducing Maker: A Simple and Fast Video Editor '}
          />
        </div>
      </div>
    </>
  );
};

export default MakerForTeamsLandingPageContainer;
